import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useSelectorToStore, useDispatchToStore } from '@hooks/reduxHooks';
import H2 from '@base/H2/H2';
import H6 from '@base/H6/H6';
import H7 from '@base/H7/H7';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import Dropdown from '@comp/Dropdown/Dropdown';
import dateHelper from '@utils/dates';
import {
  fetchEductatorScheduledClasses,
  rescheduleClass,
  updateRescheduleFormTextField,
  updateRescheduleDropdownFields,
  fetchCourseDetails
} from '@actions/educator';
import { fetchLessonList } from '@actions/courses';
import Calendar from '@icon/Calendar';
import TextField from '@base/TextField/TextField';
import SimpleClock from '@icon/SimpleClock';
import Info from '@icon/Info';
import Button from '@base/Button/Button';
import { Spacings, TextColors } from '@styles/index';

const StyCalendar = styled(Calendar)`
  margin-right: ${Spacings.SPACING_2B};
`;

const StyH7 = styled(H7)`
  grid-column: span 10;
`;

const StyButton = styled(Button)`
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_10B};
`;

const Input = styled(TextField)`
  grid-column: span 10;
  &.MuiInputBase-root {
    padding: 0px;
    .MuiInputBase-input {
      padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
    }
    .MuiInputBase-input::placeholder {
      font-size: 14px;
      color: #808080;
      font-weight: 600;
    }
    .MuiOutlinedInput-notchedOutline {
      border-color: ${({ isInvalid }) => isInvalid && TextColors.RED};
      :hover {
        border-color: ${({ isInvalid }) => isInvalid && TextColors.RED};
      }
    }
  }
  &:hover.MuiInputBase-root .MuiOutlinedInput-notchedOutline {
    border-color: ${({ isInvalid }) => isInvalid && TextColors.RED};
  }
`;

const Dropdowned = styled(Dropdown)`
  width: 464px;
`;

const StyledH6 = styled(H6)`
  margin-bottom: ${Spacings.SPACING_3B};
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_10B};
`;

const StyInfo = styled(Info)`
  margin-right: ${Spacings.SPACING_2B};
`;

const StySimpleClock = styled(SimpleClock)`
  margin-right: ${Spacings.SPACING_2B};
`;

const StyH2 = styled(H2)`
  margin-bottom: ${Spacings.SPACING_4B};
  grid-column: span 10;
`;

const StyP1 = styled(P1)`
  margin-bottom: ${Spacings.SPACING_2B};
  grid-column: span 10;
  color: ${TextColors.SECONDARY};
`;

const Immediate = styled(P2)`
  margin-bottom: ${Spacings.SPACING_2B};
  grid-column: span 10;
  display: flex;
  align-items: center;
  color: ${TextColors.SECONDARY};
`;

const DateTime = styled.div`
  margin-bottom: ${Spacings.SPACING_8B};
  grid-column: span 10;
  display: flex;
`;

const DateTimeLeft = styled(P1)`
  display: flex;
  justify-content: center;
  color: ${TextColors.SECONDARY};
  margin-right: ${Spacings.SPACING_8B};
`;

const DateTimeRight = styled(P1)`
  display: flex;
  justify-content: center;
  color: ${TextColors.SECONDARY};
`;

const allReasons = [
  { value: 11, label: 'Medical Emergency' },
  { value: 12, label: 'Scheduling issue by Unacademy team' },
  { value: 13, label: 'Internet Issue' },
  { value: 14, label: 'Payment Issue' },
  { value: 15, label: 'Personal Emergency' },
  { value: 16, label: 'Offline Classes' },
  { value: 17, label: 'Power Cut' },
  { value: 18, label: 'Travelling' },
  { value: 19, label: 'Unacademy Office Meeting' },
  { value: 20, label: 'Other' }
];

const handleInput = (id, dispatch, func) => (e) => {
  dispatch(updateRescheduleFormTextField(e.target.value, id));
  if (func) func(e.target.value, id);
};

const handleDropdownSelect = (id, dispatch) => (option) =>
  dispatch(updateRescheduleDropdownFields(option, id, false));

const getHeader = (lessonType, isTestSeries) => {
  if (lessonType !== 'quiz') return 'lesson';
  if (isTestSeries) return 'test';
  return 'quiz';
};

const RescheduleLesson = ({
  lesson,
  setRescheduleDrawer,
  courseUID,
  isSchedulePage,
  datePointer,
  isTestSeries,
  isClassPage
}) => {
  const dispatch = useDispatchToStore();
  const {
    name,
    rank,
    liveAt,
    endsAt,
    uid: lessonUID,
    type: lessonType,
    duration
  } = lesson;
  const [immediateRes, setImmediateRes] = useState(false);

  const mapStateToProps = ({ educator }) => ({
    lessonReschdeduleDetails: educator.courseDetails?.rescheduleDetails
  });
  // course details contains stats info

  const props = useSelectorToStore(mapStateToProps, false);
  const [errors, setErrors] = useState({});
  const { lessonReschdeduleDetails } = props;
  const { date, time, reasonCode, remarks } = lessonReschdeduleDetails;

  const findTimeDifference = (value, id) => {
    const resDate = new Date(
      `${id === 'date' ? value : date}T${id === 'time' ? value : time}`
    ).getTime();
    const currDate = new Date().getTime();
    if ((resDate - currDate) / (3600 * 1000) < 6) setImmediateRes(true);
    else setImmediateRes(false);
  };

  const handleButtonClick = () => {
    const newErrors = { ...errors };
    if (!date) newErrors.date = true;
    else newErrors.date = false;

    if (!time) newErrors.time = true;
    else newErrors.time = false;

    if (!reasonCode.length) newErrors.reasonCode = true;
    else newErrors.reasonCode = false;

    if (!remarks) newErrors.remarks = true;
    else newErrors.remarks = false;

    setErrors(newErrors);
    const isError = Object.values(newErrors).filter((value) => value).length;
    if (isError) return;
    const changedDate = new Date(`${date} ${time}`);
    const data = {
      item_uid: lessonUID,
      live_at: changedDate.getTime(),
      reason: reasonCode[0].value,
      remarks,
      reschedule_type: 0,
      type: lessonType
    };

    dispatch(rescheduleClass(data)).then((res) => {
      if (res.result) {
        dispatch({
          type: 'SET_SNACKBAR_MESSAGE',
          message: 'Class successfully rescheduled',
          messageType: 'success'
        });
        setRescheduleDrawer(false);
        if (!isSchedulePage && !isClassPage)
          dispatch(fetchLessonList(courseUID, true));
        else if (isClassPage) dispatch(fetchCourseDetails(courseUID));
        else dispatch(fetchEductatorScheduledClasses(datePointer, true, true));
      } else
        dispatch({
          type: 'SET_SNACKBAR_MESSAGE',
          message: res.error.message,
          messageType: 'error'
        });
    });
  };

  return (
    <>
      <StyH2>{`Reschedule ${getHeader(lessonType, isTestSeries)}`}</StyH2>
      <StyP1>{`Lesson ${rank}: ${name}`}</StyP1>
      <DateTime>
        <DateTimeLeft>
          <StyCalendar
            size={Spacings.SPACING_5B}
            color={TextColors.SECONDARY}
          />
          {dateHelper.getFullDateWithDayAtStart(liveAt)}
        </DateTimeLeft>
        <DateTimeRight>
          <StySimpleClock
            size={Spacings.SPACING_5B}
            color={TextColors.SECONDARY}
          />
          {`${dateHelper.getTime(liveAt)} -
          ${
            endsAt
              ? ` ${dateHelper.getTime(endsAt)}`
              : ` ${dateHelper.getTime(
                  dateHelper.addTime(new Date(liveAt), duration, 'm')
                )}`
          }`}
        </DateTimeRight>
      </DateTime>
      {immediateRes && (
        <Immediate>
          <StyInfo size={Spacings.SPACING_5B} />
          <span>This will be counted as immediate rescheduling</span>
        </Immediate>
      )}
      {
        <>
          <StyledH6>Reschedule to</StyledH6>
          <Input
            placeholder="Date"
            onChange={handleInput('date', dispatch, findTimeDifference)}
            value={date}
            type="date"
            isInvalid={errors?.date}
          />
          {errors?.date && (
            <StyH7 color={TextColors.RED}>
              Cannot be left blank / past date
            </StyH7>
          )}
        </>
      }
      {
        <>
          <StyledH6>Time</StyledH6>
          <Input
            placeholder="Choose time"
            onChange={handleInput('time', dispatch, findTimeDifference)}
            value={time}
            type="time"
            isInvalid={errors?.time}
          />
          {errors?.time && (
            <StyH7 color={TextColors.RED}>
              Cannot be left blank / past date
            </StyH7>
          )}
        </>
      }
      {
        <>
          <StyledH6>Reason</StyledH6>
          <Dropdowned
            options={allReasons}
            menuWidth="464px"
            placeholder="Choose a reason"
            selectedOption={reasonCode[0]}
            onOptionClick={handleDropdownSelect('reasonCode', dispatch)}
            menuColor={TextColors.BLUE}
          />
          {errors?.selectedReason && (
            <StyH7 color={TextColors.RED}>
              Please select at least one reason
            </StyH7>
          )}
        </>
      }
      {
        <>
          <StyledH6>Remarks</StyledH6>
          <Input
            placeholder="Mention any relevant details or changes to be made"
            onChange={handleInput('remarks', dispatch)}
            value={remarks}
            multiline
            rows={6}
            type="text"
            isInvalid={errors?.remarks}
          />
          {errors?.remarks && (
            <StyH7 color={TextColors.RED}>
              Remarks should be atleast 3 characters.
            </StyH7>
          )}
        </>
      }
      <StyButton
        theme="blue"
        label={`Reschedule ${getHeader(lessonType, isTestSeries)}`}
        onClick={handleButtonClick}
        grid={4}
      />
    </>
  );
};

export default RescheduleLesson;
RescheduleLesson.propTypes = {
  lesson: PropTypes.objectOf(PropTypes.any).isRequired,
  setRescheduleDrawer: PropTypes.func.isRequired,
  courseUID: PropTypes.string.isRequired,
  lessonReschdeduleDetails: PropTypes.objectOf(PropTypes.any).isRequired,
  isSchedulePage: PropTypes.bool,
  isClassPage: PropTypes.bool,
  datePointer: PropTypes.number,
  isTestSeries: PropTypes.bool
};

RescheduleLesson.defaultProps = {
  isSchedulePage: false,
  datePointer: 0,
  isTestSeries: false,
  isClassPage: false
};
